/* eslint-disable react-refresh/only-export-components */
import { createContext, PropsWithChildren, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { loginUser, rememberUser, STORAGE_AUTH_TOKEN_KEY, STORAGE_AUTH_USER_KEY } from './authService';
import { isWalkyCamSsoEmbed, notifyWalkyCamSsoError, requestWalkyCamSsoToken } from './ssoBridge';
import { AuthContextType, AuthResult, LoginParams, WalkyUser } from './types';

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: PropsWithChildren) {
  const [user, setUser] = useState<WalkyUser | null>(() => {
    try {
      const savedUser = localStorage.getItem(STORAGE_AUTH_USER_KEY);
      return savedUser ? JSON.parse(savedUser) : null;
    } catch {
      return null;
    }
  });

  const [token, setToken] = useState<string | null>(() => {
    return localStorage.getItem(STORAGE_AUTH_TOKEN_KEY) || localStorage.getItem('token') || null;
  });

  const [isLoading, setIsLoading] = useState<boolean>(true);

  // Intentar recordar la sesión con la ruta user/remember si existe un token guardado al inicio
  useEffect(() => {
    let isMounted = true;

    const restoreSession = async () => {
      // Dentro del iframe de WalkyCam, la sesión del padre tiene prioridad sobre la guardada
      const ssoToken = isWalkyCamSsoEmbed() ? await requestWalkyCamSsoToken() : null;
      const storedToken = ssoToken || localStorage.getItem(STORAGE_AUTH_TOKEN_KEY) || localStorage.getItem('token');

      if (!storedToken) {
        if (isMounted) {
          setUser(null);
          setToken(null);
          setIsLoading(false);
        }
        return;
      }

      try {
        const result = await rememberUser(storedToken);

        if (!isMounted) return;

        if (result.success && result.token) {
          setToken(result.token);
          if (result.user) {
            setUser(result.user);
            localStorage.setItem(STORAGE_AUTH_USER_KEY, JSON.stringify(result.user));
          }
          localStorage.setItem(STORAGE_AUTH_TOKEN_KEY, result.token);
        } else if (ssoToken) {
          // Token SSO rechazado: no mostrar la cuenta guardada de otro usuario
          localStorage.removeItem(STORAGE_AUTH_TOKEN_KEY);
          localStorage.removeItem(STORAGE_AUTH_USER_KEY);
          localStorage.removeItem('token');
          setUser(null);
          setToken(null);
          notifyWalkyCamSsoError();
        } else {
          // Token rechazado explícitamente por el servidor
          if (result.response_code === 100016 || result.response_code === 100002) {
            localStorage.removeItem(STORAGE_AUTH_TOKEN_KEY);
            localStorage.removeItem(STORAGE_AUTH_USER_KEY);
            localStorage.removeItem('token');
            setUser(null);
            setToken(null);
          }
        }
      } catch (err) {
        console.warn('[WalkyAuth] Session restore network check failed:', err);
        if (ssoToken && isMounted) {
          setUser(null);
          setToken(null);
          notifyWalkyCamSsoError();
        }
      } finally {
        if (isMounted) {
          setIsLoading(false);
        }
      }
    };

    void restoreSession();

    return () => {
      isMounted = false;
    };
  }, []);

  const login = useCallback(async (params: LoginParams): Promise<AuthResult> => {
    // No cambiamos isLoading a true para no desmontar el componente LoginPage durante el submit
    const result = await loginUser(params);

    if (result.success && result.token) {
      setToken(result.token);
      localStorage.setItem(STORAGE_AUTH_TOKEN_KEY, result.token);
      if (result.user) {
        setUser(result.user);
        localStorage.setItem(STORAGE_AUTH_USER_KEY, JSON.stringify(result.user));
      }
    }

    return result;
  }, []);

  const logout = useCallback(() => {
    localStorage.removeItem(STORAGE_AUTH_TOKEN_KEY);
    localStorage.removeItem(STORAGE_AUTH_USER_KEY);
    localStorage.removeItem('token');
    setUser(null);
    setToken(null);
  }, []);

  const value = useMemo<AuthContextType>(
    () => ({
      user,
      token,
      isAuthenticated: Boolean(token),
      isLoading,
      login,
      logout,
    }),
    [user, token, isLoading, login, logout],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextType {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
